// @ts-strict-ignore
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ArticleVO } from '@article/model/vo/article.vo';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors } from '@angular/forms';
import { CustomValidators } from '@shared/custom-validators';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ArrayUtils } from '@shared/util/array-utils';
import { TextureElementVO } from '@article/model/vo/texture-element.vo';

export interface TextureElementCreationData {
  textureElement: TextureElementVO;
  image: HTMLImageElement;
}

@Component({
  standalone: false,
  selector: 'new-texture-element-popup',
  templateUrl: './new-texture-element-popup.component.html'
})
export class NewTextureElementPopupComponent implements OnInit {

  public static readonly MAX_WIDTH_CONTAINER: number = 300;
  public static readonly MAX_HEIGHT_CONTAINER: number = 200;

  @Input() public article: ArticleVO;
  @Output() public finish = new EventEmitter<TextureElementCreationData>();

  private textureElements: TextureElementVO[];
  private _imageFile: any;
  private image: HTMLImageElement;
  public imageUrl: string;
  public imageContainerWidth: number = 0;
  public imageContainerHeight: number = 0;

  public textureNamePrefix: string = TextureElementVO.TEXTURE_ELEMENT_PREFIX;

  public form: FormGroup;

  constructor(private fb: FormBuilder,
              private bsModalRef: BsModalRef,
              private localeResourceManager: LocaleResourceManager) { }

  public ngOnInit(): void {
    this.textureElements = this.article.textureElements;
    this.form = this.fb.group({
      autoGenerateTextureName: [false],
      textureName: [{ value: '', disabled: true }, [this.textureNameValidate.bind(this), CustomValidators.trimRequired]],
    });
    this.autoGenerateTextureNameHandler();
  }

  public autoGenerateTextureNameHandler(): void {
    if (this.form.get('autoGenerateTextureName').value) {
      this.form.get('textureName').setValue(this.generateNewTextureName());
      this.form.get('textureName').disable();
    } else {
      this.form.get('textureName').enable();
    }
  }

  private generateNewTextureName(): string {
    let nameCounter: number = 1;
    while (!this.isTextureNameValid(nameCounter.toString())) {
      nameCounter++;
    }
    return nameCounter.toString();
  }

  public isTextureNameValid(testName: string): boolean {
    testName = this.textureNamePrefix + testName;
    for (const texture of this.textureElements) {
      if (texture.name === testName) {
        return false;
      }
    }
    return true;
  }

  public textureNameValidate(c: FormControl): ValidationErrors | null {
    if (this.form.get('autoGenerateTextureName').value || c.value.disabled) {
      return;
    }
    if (!this.isTextureNameValid(c.value.trim())) {
      return { messageKey: `admintool.error.doubleValueNotAllowed` };
    }
  }

  public getInputError(c: AbstractControl): string {
    if (c && c.hasError('messageKey')) {
      return this.localeResourceManager.getValueByKey(c.getError('messageKey'), { 0: c.getError('param') });
    } else {
      return '';
    }
  }

  public imageUpload(e) {
    const reader: FileReader = new FileReader();
    reader.onloadend = () => {
      const img: HTMLImageElement = new Image();
      img.onload = () => {
        this.imageUrl = reader.result as string;
        if (img) {
          this.resizeImageContainer(img.width, img.height);
          this.image = img;
        }
      };
      img.src = reader.result as string;
    };
    if (ArrayUtils.isNotEmpty(e.target.files) && /(png|jpg|jpeg)/.test(e.target.files[0].type)) {  // only png, jpg, jpeg types are allowed
      this._imageFile = e.target.files[0];
      reader.readAsDataURL(this._imageFile);
    }
  }

  private resizeImageContainer(widthImage: number, heightImage: number): void {
    if (widthImage && heightImage) {
      const differenceWidth = widthImage / NewTextureElementPopupComponent.MAX_WIDTH_CONTAINER;
      const differenceHeight = heightImage / NewTextureElementPopupComponent.MAX_HEIGHT_CONTAINER;
      const maxDifference: number = Math.max(differenceWidth, differenceHeight );
      this.imageContainerWidth = Math.ceil(widthImage / maxDifference);
      this.imageContainerHeight = Math.ceil(heightImage / maxDifference);
    }
  }

  public isSubmitAvailable(): boolean {
    return this.form.valid && !!this.image;
  }

  public save(): void {
    const textureName: string = this.textureNamePrefix + this.form.get('textureName').value.trim();
    const textureElement: TextureElementVO = new TextureElementVO(textureName, this._imageFile.name);
    const data: TextureElementCreationData = {
      textureElement,
      image: this.image
    };
    this.close(data);
  }

  public close(data: TextureElementCreationData = null): void {
    this.bsModalRef.hide();
    this.finish.emit(data);
  }
}
